import type { EventStatus } from '@/api/events';
import { eventsApi } from '@/api/events';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Fetch events.
 *
 * Pass `projectId: null | undefined` to fetch **all** events (global view).
 * The `enabled` flag is always true so that the all-projects view works;
 * callers that want to suppress the query entirely should handle that at
 * the component level.
 */
export function useEvents(
  projectId: string | null | undefined,
  params: { status?: EventStatus | EventStatus[]; limit?: number; q?: string } = {},
) {
  return useQuery({
    queryKey: ['events', projectId ?? null, params],
    queryFn: () => eventsApi.list({ project: projectId ?? undefined, ...params }),
    staleTime: 0,
  });
}

/**
 * How many dumps landed in the last `hours`, across every project the caller
 * can see — the rail badge's single number.
 *
 * It polls instead of streaming: the badge answers "is anything still coming
 * in?", and a rolling 24h window only moves when a machine uploads, so a
 * minute of lag is invisible while a socket would be live weight on every page.
 * The key starts with `events`, so the invalidation that follows an upload
 * refreshes it too.
 */
export function useRecentEventCount(hours = 24) {
  return useQuery({
    queryKey: ['events', 'recent-count', hours],
    queryFn: () => eventsApi.recentCount(hours),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/**
 * Cursor-paginated events: pages accumulate via next_cursor so lists longer
 * than one page are not silently truncated.
 */
export function useEventsInfinite(
  projectId: string | null | undefined,
  params: { status?: EventStatus | EventStatus[]; limit?: number; q?: string } = {},
) {
  return useInfiniteQuery({
    queryKey: ['events', projectId ?? null, params],
    queryFn: ({ pageParam }) =>
      eventsApi.list({
        project: projectId ?? undefined,
        ...params,
        cursor: pageParam ?? undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    staleTime: 0,
  });
}

export function useReanalyzeEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => eventsApi.reanalyze(eventId),
    onSuccess: (_, eventId) => {
      qc.invalidateQueries({ queryKey: ['event', eventId] });
      qc.invalidateQueries({ queryKey: ['event-runs', eventId] });
    },
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => eventsApi.remove(eventId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['events'] }),
  });
}

export function useUpdateEventTags(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tags: string[]) => eventsApi.updateTags(eventId, tags),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['event', eventId] }),
  });
}

export function useUpdateEventDescription(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (description: string | null) => eventsApi.updateDescription(eventId, description),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['event', eventId] }),
  });
}
